import type { layout } from "@app/domain";
import type { HeldGift, SeatInfo, TableStatus, Variant } from "@app/protocol";
import { useCallback, useState } from "react";
import { Slot } from "../canvas/Stage";
import { useStore } from "../store";
import { cardSide, cardStyle, type SeatSlot } from "./cardPlacement";
import { GiftFlights } from "./GiftFlights";
import { Ornament } from "./Ornament";
import { Pod, type SeatLook } from "./Pod";
import { PodCard } from "./PodCard";
import { SeatCardSocial, SeatOverlay } from "./SeatSocial";
import type { seatSlots } from "./seatOrder";
import { giftFor } from "./social";
import { useRankFrames } from "./useRankFrames";

/** One player card at a time; it shuts when its player leaves the seat (U8) or the stage changes (U9). */
function useOpenCard(seats: readonly (SeatInfo | null)[], status: TableStatus) {
  const [open, setOpen] = useState<string | null>(null);
  const [stage, setStage] = useState(status);
  if (stage !== status) {
    setStage(status);
    setOpen(null);
  }
  const seated = seats.some((seat) => seat !== null && seat.userId === open);
  if (open !== null && !seated) setOpen(null);
  // Stable, so an open card does not refocus on every snapshot.
  const close = useCallback(() => setOpen(null), []);
  return { open, setOpen, close };
}

/** Gifts this viewer may see: none when hidden here, none from muted senders (decisions 143, 154). */
function useVisibleGifts(gifts: Readonly<Record<string, HeldGift>>): Record<string, HeldGift> {
  const muted = useStore((s) => s.muted);
  const hideGifts = useStore((s) => s.hideGifts);
  return Object.fromEntries(
    Object.keys(gifts).flatMap((userId) => {
      const held = giftFor(gifts, userId, muted, hideGifts);
      return held === null ? [] : [[userId, held] as const];
    }),
  );
}

/**
 * The four seats at their canvas places, each a pod; `look` says what each one shows. Pods carry the
 * player's card (review 0017), the gift they hold and their latest reaction (review 0022).
 */
export function SeatPods({
  seats,
  slots,
  canvas,
  look,
  variant,
  status,
  gifts,
}: {
  /** The table's game: its podiums frame the players (review 0014, decision 68). */
  variant: Variant;
  seats: readonly (SeatInfo | null)[];
  slots: ReturnType<typeof seatSlots>;
  canvas: layout.Canvas;
  look: (seat: SeatInfo | null, index: number) => SeatLook;
  status: TableStatus;
  gifts: Readonly<Record<string, HeldGift>>;
}) {
  const rankOf = useRankFrames(variant);
  const { open, setOpen, close } = useOpenCard(seats, status);
  const visible = useVisibleGifts(gifts);
  const [landing, setLanding] = useState<ReadonlySet<string>>(new Set());
  const dense = canvas.state !== "wide";
  const seatOf = (userId: string) => seats.find((seat) => seat?.userId === userId) ?? null;
  const heldBy = (seat: SeatInfo) =>
    landing.has(seat.userId) ? null : (visible[seat.userId] ?? null);
  return (
    <>
      {(Object.keys(slots) as SeatSlot[]).map((slot) => {
        const seat = seats[slots[slot]] ?? null;
        const seatLook = look(seat, slots[slot]);
        const isOpen = seat !== null && seat.userId === open;
        const held = seat === null ? null : heldBy(seat);
        return (
          <Slot key={slot} rect={canvas.elements[slot]} className={isOpen ? "z-30" : "z-10"}>
            {/* `data-pod` lets the deal find the circle to shake as cards arrive. */}
            <div data-pod={slot} className="h-full w-full">
              <Pod
                seat={seat}
                look={seatLook}
                frame={canvas.pod}
                dense={dense}
                rank={rankOf(seat)}
                overlay={
                  seat && (
                    <SeatOverlay
                      seat={seat}
                      held={held}
                      sender={held && seatOf(held.from)}
                      size={canvas.pod.size}
                      slot={slot}
                      gap={canvas.pod.pillDrop + 12}
                    />
                  )
                }
                card={{
                  open: isOpen,
                  onToggle: () => setOpen(isOpen || seat === null ? null : seat.userId),
                  render: (opener) =>
                    seat && (
                      <PodCard
                        seat={seat}
                        rank={rankOf(seat)}
                        action={seatLook.action}
                        style={cardStyle(cardSide(slot), canvas.pod.pillDrop + 22)}
                        dense={dense}
                        opener={opener}
                        onClose={close}
                        social={<SeatCardSocial seat={seat} seats={seats} onSent={close} />}
                      />
                    ),
                }}
              />
            </div>
          </Slot>
        );
      })}
      <Ornament>
        <GiftFlights
          canvas={canvas}
          slots={slots}
          seats={seats}
          gifts={visible}
          onLanding={setLanding}
        />
      </Ornament>
    </>
  );
}
